import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { ClipboardList, Plus, Trash2, ArrowLeft, Save, Printer, Download, X, Check, Ban, Factory } from 'lucide-react';
import QuickAddCustomerButton from '../../components/QuickAddCustomerDialog';
import { toast } from 'sonner';
import { DocHistoryPanel } from '../../components/orders/DocHistoryPanel';
import { DocOverrideActions } from '../../components/orders/DocOverrideActions';
import DocumentChainStrip from '../../components/DocumentChainStrip';
import LockedDocBanner from '../../components/LockedDocBanner';
import ProductionMaterialsPanel from '../../components/ProductionMaterialsPanel';
import EvidencePhotoGallery from '../../components/EvidencePhotoGallery';

const META = {
  Draft:      { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Issued:     { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  InProgress: { bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  Completed:  { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:     { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled:   { bg: 'bg-red-100 text-red-800 border-red-200' },
};
const PRIORITIES = ['Rush', 'High', 'Normal', 'Low'];
const newLine = () => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, bom_id: '', description: '', quantity: 1, notes: '' });

const ProductionOrderDetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromSo = params.get('from_so');
  const dupId = params.get('dup');
  const isNew = !id || id === 'new';
  const [po, setPo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [sos, setSos] = useState([]);
  const [boms, setBoms] = useState([]);
  const [form, setForm] = useState({
    customer_id: '', sales_order_id: '', priority: 'Normal',
    requested_completion_date: '', notes: '',
    project_code: '', drawing_revision: '', materials_release_date: '', shop_floor_notes: '',
    lines: [newLine()],
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s, b] = await Promise.all([api().get('/customers'), api().get('/sales-orders'), api().get('/boms')]);
      setCustomers(c.data || []); setSos(s.data || []); setBoms(b.data || []);
      if (!isNew || dupId) {
        const r = await api().get(`/production-orders/${isNew ? dupId : id}`);
        if (isNew) toast.success(`Copied from ${r.data.po_number}. Review, then save the new Draft`);
        setPo(isNew ? null : r.data);
        setForm({
          customer_id: r.data.customer_id,
          sales_order_id: r.data.sales_order_id || '',
          priority: r.data.priority || 'Normal',
          requested_completion_date: r.data.requested_completion_date || '',
          notes: r.data.notes || '',
          project_code: r.data.project_code || '',
          drawing_revision: r.data.drawing_revision || '',
          materials_release_date: r.data.materials_release_date || '',
          shop_floor_notes: r.data.shop_floor_notes || '',
          lines: (r.data.lines || []).map(l => ({ ...l })),
        });
      } else if (fromSo) {
        // Pre-fill from a Sales Order
        try {
          const r = await api().post(`/production-orders/from-sales-order/${fromSo}`);
          toast.success(`${r.data.po_number} draft created from SO ${r.data.sales_order_number_snapshot}`);
          navigate(`/orders/production/${r.data.id}`, { replace: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); navigate('/orders/sales'); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [api, id, isNew, fromSo, dupId, navigate]);
  useEffect(() => { load(); }, [load]);

  const editable = isNew || (po && (po.status === 'Draft' || po.status === 'Issued'));
  const addLine = () => setForm({ ...form, lines: [...form.lines, newLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) });
  const fillFromBom = (i, bomId) => {
    const b = boms.find(x => x.id === bomId);
    if (!b) return;
    updateLine(i, { bom_id: b.id, description: b.product_name || b.name || b.bom_name || `BOM ${b.id}` });
  };

  const save = async () => {
    if (!form.customer_id) { toast.error('Pick a customer'); return; }
    const valid = form.lines.filter(l => l.bom_id && (l.description || '').trim() && Number(l.quantity) > 0);
    if (!valid.length) { toast.error('Add at least one BOM line with qty > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        sales_order_id: form.sales_order_id || null,
        priority: form.priority,
        requested_completion_date: form.requested_completion_date || null,
        project_code: form.project_code || null,
        drawing_revision: form.drawing_revision || null,
        materials_release_date: form.materials_release_date || null,
        shop_floor_notes: form.shop_floor_notes || null,
        notes: form.notes || null,
        lines: valid.map(l => ({ bom_id: l.bom_id, description: l.description, quantity: Number(l.quantity), notes: l.notes || null })),
      };
      if (isNew) {
        const r = await api().post('/production-orders', payload);
        toast.success(`${r.data.po_number} created as Draft`);
        navigate(`/orders/production/${r.data.id}`, { replace: true });
      } else {
        const r = await api().put(`/production-orders/${id}`, payload);
        setPo(r.data); toast.success(`${r.data.po_number} saved`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(true);
    try { const r = await api().post(`/production-orders/${id}/${action}`, extra); setPo(r.data); toast.success(`${r.data.po_number} → ${r.data.status}`); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); } finally { setActing(false); }
  };

  const spawnWos = async () => {
    if (!window.confirm(`Spawn Work Orders for every unspawned line on ${po.po_number}? This will flip the PO to InProgress.`)) return;
    try { const r = await api().post(`/production-orders/${id}/spawn-work-orders`); setPo(r.data); const count = (r.data.lines || []).filter(l => l.work_order_id).length; toast.success(`${count} Work Order(s) linked. PO is now ${r.data.status}.`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); }
  };


  const printPo = () => {
    const base = api().defaults.baseURL; const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank'); if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/production-orders/${id}/print`, { headers: { Authorization: token } })
      .then(r => r.text()).then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };

  const download = async (fmt) => {
    try {
      const res = await api().get(fmt === 'pdf' ? `/production-orders/${id}/pdf` : `/production-orders/${id}/export.${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : (fmt === 'json' ? 'application/json' : 'text/csv') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  // Compute live progress from existing lines
  const WEIGHT = { Completed: 1, Closed: 1, InProgress: 0.5, Paused: 0.5, Released: 0.1, Draft: 0 };
  const totalQty = (po?.lines || []).reduce((a, l) => a + Number(l.quantity || 0), 0);
  const weightedQty = (po?.lines || []).reduce((a, l) => a + Number(l.quantity || 0) * (WEIGHT[l.work_order_status] || 0), 0);
  const progressPct = totalQty ? Math.round(1000 * weightedQty / totalQty) / 10 : 0;
  const spawnedCount = (po?.lines || []).filter(l => l.work_order_id).length;
  const totalLines = (po?.lines || []).length;

  const statusPill = po && <Badge className={`${(META[po.status] || META.Draft).bg} border rounded-sm text-xs uppercase tracking-wider`} data-testid="po-status-pill">{po.status}</Badge>;

  return (
    <div className="space-y-4" data-testid="production-order-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/production')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-slate-700" />
              {isNew ? 'New Production Order' : po?.po_number || 'Loading…'}
              {statusPill}
            </h1>
            {po && <p className="text-xs text-slate-500 mt-1">
              {po.customer_name_snapshot}{po.sales_order_number_snapshot && <> · Linked SO: <strong>{po.sales_order_number_snapshot}</strong></>}
            </p>}
          </div>
        </div>
        {po && (
          <div className="flex items-center gap-1 flex-wrap">
            <Button variant="outline" size="sm" onClick={printPo} className="rounded-sm" data-testid="po-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={() => download('pdf')} className="rounded-sm" data-testid="po-export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => download('json')} className="rounded-sm" data-testid="po-export-json-btn"><Download className="w-3.5 h-3.5 mr-1" /> JSON</Button>
            <Button variant="outline" size="sm" onClick={() => download('csv')} className="rounded-sm" data-testid="po-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            {po.status === 'Draft' && <Button size="sm" onClick={() => act('issue')} disabled={acting} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="po-issue-btn"><Check className="w-3.5 h-3.5 mr-1" /> Issue</Button>}
            {po.status === 'Issued' && <Button size="sm" onClick={spawnWos} disabled={acting} className="bg-amber-600 hover:bg-amber-700 rounded-sm" data-testid="po-spawn-wos-btn"><Factory className="w-3.5 h-3.5 mr-1" /> Spawn Work Orders</Button>}
            {['Issued', 'InProgress'].includes(po.status) && <Button size="sm" onClick={() => act('complete')} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="po-complete-btn"><Check className="w-3.5 h-3.5 mr-1" /> Complete</Button>}
            {['InProgress', 'Completed'].includes(po.status) && <Button size="sm" variant="outline" onClick={() => navigate(`/orders/packing/new?from_po=${po.id}`)} className="border-sky-300 text-sky-700 hover:bg-sky-50 rounded-sm" data-testid="po-spawn-ps-btn">⇢ Create Packing Slip</Button>}
            {po.status === 'Completed' && <Button size="sm" onClick={() => act('close')} disabled={acting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-close-btn"><Check className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            <DocOverrideActions docType="production-orders" docId={id} label="Production Order" number={po.po_number} status={po.status} cancelable={['Draft', 'Issued', 'InProgress', 'Completed'].includes(po.status)} testPrefix="po" onAction={act} onReload={load} onDeleted={() => navigate('/orders/production')} />
          </div>
        )}
      </div>

      {!isNew && <DocumentChainStrip api={api} docType="production_order" docId={id} />}
      {po && !editable && <LockedDocBanner doc={po} label="Production Order" onDuplicate={() => navigate(`/orders/production/new?dup=${id}`)} />}

      {po && (
        <Card className="industrial-card" data-testid="po-progress-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Progress — {progressPct}%</div>
              <div className="text-xs text-slate-500">{spawnedCount}/{totalLines} lines have a Work Order spawned</div>
            </div>
            <Progress value={progressPct} className="h-2" />
          </CardContent>
        </Card>
      )}

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Header</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="po-customer-select"><SelectValue placeholder="Pick a customer…" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
              </div>
              <QuickAddCustomerButton api={api} disabled={!editable} testId="po-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))); setForm(f => ({ ...f, customer_id: c.id })); }} />
            </div>
          </div>
          <div className="col-span-2">
            <Label>Linked Sales Order (optional)</Label>
            <Select value={form.sales_order_id || 'none'} onValueChange={(v) => setForm({ ...form, sales_order_id: v === 'none' ? '' : v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="po-so-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— no linked SO —</SelectItem>{sos.map(s => <SelectItem key={s.id} value={s.id}>{s.so_number} — {s.customer_name_snapshot}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="po-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Target Completion Date</Label><Input type="date" value={form.requested_completion_date} onChange={(e) => setForm({ ...form, requested_completion_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="po-target-date" /></div>
          <div><Label>Project Code</Label><Input value={form.project_code} onChange={(e) => setForm({ ...form, project_code: e.target.value })} placeholder="PRJ-2026-001" disabled={!editable} className="rounded-sm mono" data-testid="po-project-code" /></div>
          <div><Label>Drawing Rev</Label><Input value={form.drawing_revision} onChange={(e) => setForm({ ...form, drawing_revision: e.target.value })} placeholder="Rev C" disabled={!editable} className="rounded-sm mono" data-testid="po-drawing-rev" /></div>
          <div><Label>Materials Release Date</Label><Input type="date" value={form.materials_release_date} onChange={(e) => setForm({ ...form, materials_release_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="po-materials-release" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Shop Floor Notes</Label><Textarea rows={2} value={form.shop_floor_notes} onChange={(e) => setForm({ ...form, shop_floor_notes: e.target.value })} placeholder="Routing hints, tooling callouts, QA requirements…" disabled={!editable} className="rounded-sm" data-testid="po-shop-floor-notes" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="po-notes" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">BOM Lines (what to produce)</CardTitle>
          {editable && <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm" data-testid="po-add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add BOM Line</Button>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>BOM *</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead>
              <TableHead>Work Order</TableHead><TableHead>WO Status</TableHead>
              {editable && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {form.lines.map((ln, i) => (
                <TableRow key={ln.id} data-testid={`po-line-${i}`}>
                  <TableCell className="min-w-[180px]">
                    <Select value={ln.bom_id || ''} onValueChange={(v) => fillFromBom(i, v)} disabled={!editable || !!ln.work_order_id}>
                      <SelectTrigger className="rounded-sm text-xs"><SelectValue placeholder="— pick BOM —" /></SelectTrigger>
                      <SelectContent className="max-h-72">{boms.map(b => <SelectItem key={b.id} value={b.id}>{b.product_name || b.name || b.bom_name || b.id}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="min-w-[220px]"><Input value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} disabled={!editable} className="rounded-sm text-xs" /></TableCell>
                  <TableCell className="text-right w-20"><Input type="number" min="1" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} disabled={!editable || !!ln.work_order_id} className="rounded-sm text-xs text-right mono" data-testid={`po-line-qty-${i}`} /></TableCell>
                  <TableCell className="text-xs mono">{ln.work_order_number || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-xs">{ln.work_order_status ? <Badge variant="outline" className="rounded-sm text-xs">{ln.work_order_status}</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                  {editable && <TableCell className="w-10"><Button variant="ghost" size="icon" onClick={() => removeLine(i)} disabled={!!ln.work_order_id} className="text-red-600 h-7 w-7"><X className="w-3.5 h-3.5" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editable && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create Draft' : 'Save Changes')}
          </Button>
        </div>
      )}

      {!isNew && <ProductionMaterialsPanel api={api} poId={id} />}
      {!isNew && (
        <EvidencePhotoGallery
          api={api}
          docType="production_order"
          docId={id}
          evidenceType="work_evidence"
          title="Work Evidence Photos"
          emptyHint="Snap photos as the shop floor completes work. They appear on the printed PO and PDF."
          testIdPrefix="po-evidence"
        />
      )}
      {!isNew && po && <DocHistoryPanel entityType="ProductionOrder" docId={id} refreshKey={`${po.status}-${po.updated_at}`} />}
    </div>
  );
};

export default ProductionOrderDetailPage;
